import { all, extend } from 'underscore';
import { Campaign, AdGroup, DynamicSearchAd } from '@bingads-webui/campaign-odata-schemas';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import CampaignCacheManager from '@bingads-webui-campaign-legacy/cache-manager';
import { isBulkWrappedResponse } from '../shared/bulk';
import { ERROR_ENTITY, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '../shared/bulk-upsert-errors';
import { GENERIC_ERROR_MESSAGE_KEY } from '../shared/constants';
import { viewModelToModelForDSA, bulkModelToViewModelForDSA } from './dynamic-search-ad';

const adResource = new (edmCampaign.types.resolve('Model.DynamicSearchAd')).ResourceIdentifier();

const createOrUpdateAd = (ad) => {
  const createOrUpdateCall = ad && ad.Id ?
    adResource.bulkUpdate.bind(adResource) :
    adResource.bulkCreate.bind(adResource);
  return createOrUpdateCall(ad);
};

export const deleteAd = (adId, options) => {
  const destroyCall = adResource.bulkDestroy.bind(adResource);
  return destroyCall(adId, options);
};

const bulkUpsertAdEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
  [getBulkUploadErrorType(DynamicSearchAd)]: ERROR_ENTITY.DynamicSearchAd,
};

const errorProcessor = error => extend({}, error, {
  Property: error.property,
  Message: error.message,
  Code: error.code,
});

export const saveAd = ({
  ad: adViewModel,
  campaignId,
  adGroupId,
}, {
  i18n,
  scenario,
  activityLogger,
}) => {
  const adModel = viewModelToModelForDSA(adViewModel, campaignId, adGroupId);

  return createOrUpdateAd(adModel)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then((result) => {
      const data = bulkModelToViewModelForDSA({
        adModel: result,
      });
      return { data };
    }, (bulkOrSystemError) => {
      const { entity, errors } = bulkOrSystemError;

      // Ignore errors if the entity was eventually created and all the errors are editorial errors
      if (entity && entity.Id && all(errors, error => error.Error === 'CampaignServiceEditorialValidationError')) {
        CampaignCacheManager.invalidate('Ad'); // Cache is not automatically invalidated since the API failed
        return {
          data: bulkModelToViewModelForDSA({
            adModel: entity,
          }),
          editorialErrors: errors,
        };
      }

      if (isBulkWrappedResponse(bulkOrSystemError)) {
        let filteredErrors = errors;
        if (entity.Id <= 0) {
          filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
        }

        const serverErrors = modelToViewModelForBulkErrors(
          filteredErrors,
          bulkUpsertAdEntityErrorMap,
          scenario,
          i18n,
          activityLogger,
          errorProcessor
        );

        // eslint-disable-next-line prefer-promise-reject-errors
        return {
          data: bulkModelToViewModelForDSA({
            adModel: entity,
          }),
          serverErrors,
        };
      }

      // Activity
      // eslint-disable-next-line no-console
      console.warn(`Campaign bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
      // eslint-disable-next-line prefer-promise-reject-errors
      return {
        data: adViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      };
    });
};
